import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, API } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Stars, RATING_KEYS, RATING_LABELS } from "../components/Ratings";
import {
  Check, X, FileText, AlertTriangle, ShieldAlert, PencilLine, Trash2, Image as ImageIcon,
  ClipboardList, Inbox, BarChart3, MessageSquare, Mail, Send, CheckCircle2, Users, MapPin, GitMerge, CreditCard, Clock,
} from "lucide-react";

function DocViewer({ fileId }) {
  const [url, setUrl] = useState(null);
  const [type, setType] = useState("");
  useEffect(() => {
    let revoke;
    api.get(`/files/${fileId}/download`, { responseType: "blob" }).then((res) => {
      const b = URL.createObjectURL(res.data); revoke = b; setUrl(b); setType(res.data.type);
    }).catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [fileId]);
  if (!url) return <div className="h-72 flex items-center justify-center text-slate-400 text-sm">Loading document…</div>;
  if (type.includes("pdf")) return <iframe title="doc" src={url} className="w-full h-[26rem] rounded-lg border border-slate-200" data-testid="doc-pdf" />;
  return <img src={url} alt="document" className="w-full rounded-lg border border-slate-200" data-testid="doc-image" />;
}

const SECTIONS = [
  { k: "reviews", label: "Reviews", icon: ClipboardList },
  { k: "moderation", label: "Moderation", icon: Clock },
  { k: "disputes", label: "Disputes", icon: ShieldAlert },
  { k: "claims", label: "Claims", icon: Inbox },
  { k: "reverify", label: "Re-verify", icon: AlertTriangle },
  { k: "users", label: "Users", icon: Users },
  { k: "abandoned", label: "Abandoned", icon: FileText },
  { k: "memberships", label: "Memberships", icon: CreditCard },
  { k: "stripe", label: "Stripe", icon: CreditCard, senior: true },
  { k: "properties", label: "Properties", icon: MapPin },
  { k: "messages", label: "Messages", icon: MessageSquare },
  { k: "analytics", label: "Analytics", icon: BarChart3 },
];

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("reviews");
  const isSenior = user?.role === "senior_admin";
  const sections = SECTIONS.filter((s) => !s.senior || isSenior);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "admin" && user.role !== "senior_admin") navigate("/");
  }, [user, loading, navigate]);

  return (
    <div className="App bg-[#F4F7FA] min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 py-10 flex flex-col md:flex-row gap-5 md:gap-8">
        {/* Mobile nav */}
        <div className="md:hidden -mx-5 px-5 overflow-x-auto" data-testid="admin-mobile-nav">
          <nav className="flex gap-2 pb-1 w-max">
            {sections.map((s) => (
              <button key={s.k} data-testid={`admin-nav-m-${s.k}`} onClick={() => setSection(s.k)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${section === s.k ? "bg-navy text-white" : "bg-white text-slate-600 border border-slate-200"}`}>
                <s.icon className="w-4 h-4" /> {s.label}
              </button>
            ))}
          </nav>
        </div>
        <aside className="w-48 shrink-0 hidden md:block">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] font-mono text-mint-dark">Admin</span>
          <nav className="mt-4 flex flex-col gap-1">
            {sections.map((s) => (
              <button key={s.k} data-testid={`admin-nav-${s.k}`} onClick={() => setSection(s.k)}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${section === s.k ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                <s.icon className="w-4 h-4" /> {s.label}
              </button>
            ))}
          </nav>
        </aside>
        <main className="flex-1 min-w-0">
          {section === "reviews" && <ReviewsSection />}
          {section === "moderation" && <ModerationSection />}
          {section === "disputes" && <DisputesSection />}
          {section === "claims" && <ClaimsSection />}
          {section === "reverify" && <ReverifySection />}
          {section === "users" && <UsersSection />}
          {section === "abandoned" && <AbandonedSection />}
          {section === "memberships" && <MembershipsSection />}
          {section === "stripe" && <StripeSection />}
          {section === "properties" && <PropertiesSection />}
          {section === "messages" && <MessagesSection />}
          {section === "analytics" && <AnalyticsSection />}
        </main>
      </div>
    </div>
  );
}

function PropertyPhoto({ slug, version }) {
  const [err, setErr] = useState(false);
  useEffect(() => { setErr(false); }, [version]);
  if (err) return (
    <div className="h-40 flex flex-col items-center justify-center text-slate-400 text-sm border border-dashed border-slate-300 rounded-lg gap-2" data-testid={`no-photo-${slug}`}>
      <ImageIcon className="w-5 h-5" /> No photo set
    </div>
  );
  return <img src={`${API}/properties/${slug}/photo?v=${version}`} alt="property" onError={() => setErr(true)}
    className="w-full h-40 object-cover rounded-lg border border-slate-200" data-testid={`property-photo-${slug}`} />;
}

function ReviewsSection() {
  const { user } = useAuth();
  const isSenior = user?.role === "senior_admin";
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [town, setTown] = useState("");
  const [reviews, setReviews] = useState([]);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({ title: "", body: "" });
  const [notifyId, setNotifyId] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [photoVer, setPhotoVer] = useState({});

  const load = useCallback(() => {
    const params = new URLSearchParams({ status, q, town });
    api.get(`/admin/reviews?${params}`).then((r) => setReviews(r.data)).catch(() => {});
  }, [status, q, town]);
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const approve = async (id) => { if (!window.confirm("Approve this review and publish it live?")) return; await api.post(`/admin/reviews/${id}/approve`); load(); };
  const reject = async (id) => { if (!window.confirm("Reject this review? The tenant will be notified.")) return; await api.post(`/admin/reviews/${id}/reject`, { reason: rejectReason }); setRejectId(null); setRejectReason(""); load(); };
  const takedown = async (id) => { if (!window.confirm("Take down this live review? It will be hidden from the public.")) return; await api.post(`/admin/reviews/${id}/takedown`, { reason: "Removed by admin" }); load(); };
  const deleteReview = async (id) => { if (!window.confirm("PERMANENTLY delete this review? This cannot be undone and the review is removed from the database entirely.")) return; try { await api.delete(`/admin/reviews/${id}`); load(); } catch (err) { alert(formatApiError(err.response?.data?.detail)); } };
  const restoreReview = async (id) => { if (!window.confirm("Make this review live again on the public site?")) return; try { await api.post(`/admin/reviews/${id}/restore`); load(); } catch (err) { alert(formatApiError(err.response?.data?.detail)); } };
  const saveEdit = async (id) => { await api.patch(`/admin/reviews/${id}`, edit); setEditId(null); load(); };
  const changePhoto = async (slug, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("photo", file);
    await api.post(`/admin/properties/${slug}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    setPhotoVer((v) => ({ ...v, [slug]: (v[slug] || 0) + 1 }));
    load();
  };
  const notifyLandlord = async (id) => {
    await api.post(`/admin/reviews/${id}/notify-landlord`, { name: "Tenverly Admin", email: notifyEmail, subject: "", message: "" });
    setNotifyId(null); setNotifyEmail("");
  };

  return (
    <div>
      <h1 className="font-heading font-extrabold text-3xl text-navy">Review moderation</h1>

      <div className="mt-5 flex gap-3 flex-wrap items-end" data-testid="admin-review-filters">
        <div>
          <label className="text-xs font-semibold text-slate-500">Status</label>
          <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="block mt-1 border border-slate-300 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint">
            {["pending", "live", "rejected", "removed", "all"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <input data-testid="filter-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search text / tenant / address" className="border border-slate-300 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
        <input data-testid="filter-town" value={town} onChange={(e) => setTown(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Filter by town" className="border border-slate-300 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
        <button data-testid="filter-apply" onClick={load} className="bg-navy text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-navy-600 transition-colors">Search</button>
      </div>

      <div className="mt-6 flex flex-col gap-6" data-testid="admin-reviews">
        {reviews.length === 0 && <p className="text-slate-500" data-testid="admin-empty">No reviews match.</p>}
        {reviews.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden grid lg:grid-cols-2 shadow-sm" data-testid={`admin-review-${r.id}`}>
            <div className="p-6 border-b lg:border-b-0 lg:border-r border-slate-200">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-xs font-mono uppercase tracking-wider text-slate-500">{r.full_address}</p>
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-600">{r.status}</span>
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-navy">{r.tenant_name}</span>
                <Stars value={r.overall} /><span className="font-mono text-sm">{r.overall.toFixed(1)}</span>
                {r.edited && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-semibold flex items-center gap-1"><PencilLine className="w-3 h-3" /> edited{r.edited_by ? ` by ${r.edited_by}` : ""}</span>}
              </div>
              {r.toxicity?.flagged && (
                <div className="mt-3 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm" data-testid={`toxicity-flag-${r.id}`}>
                  <ShieldAlert className="w-4 h-4" /> AI flagged: {r.toxicity.reason || "potential toxicity"}
                </div>
              )}

              {editId === r.id ? (
                <div className="mt-3 flex flex-col gap-2" data-testid={`edit-box-${r.id}`}>
                  <input data-testid={`edit-title-${r.id}`} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                    className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                  <textarea data-testid={`edit-body-${r.id}`} rows={4} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })}
                    className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                  <div className="flex gap-2">
                    <button data-testid={`edit-save-${r.id}`} onClick={() => saveEdit(r.id)} className="bg-mint text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-mint-dark">Save edit</button>
                    <button onClick={() => setEditId(null)} className="text-sm text-slate-500 px-3">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  {r.title && <h4 className="mt-3 font-semibold text-navy">{r.title}</h4>}
                  <p className="mt-1 text-slate-700 whitespace-pre-line">{r.body}</p>
                  {isSenior && r.edited && r.original_body && (
                    <details className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm" data-testid={`original-review-${r.id}`}>
                      <summary className="cursor-pointer font-semibold text-amber-900">View original review (before {r.edited_by === "tenant" ? "tenant" : "admin"} edit)</summary>
                      {r.original_title && <p className="mt-2 font-semibold text-amber-900">{r.original_title}</p>}
                      <p className="mt-1 text-amber-800 whitespace-pre-line">{r.original_body}</p>
                    </details>
                  )}
                </>
              )}
              <p className="mt-2 text-xs font-mono text-slate-400">Tenancy: {r.tenancy_start || "?"} → {r.tenancy_end || "present"} · Managed by: {r.responsible_party === "letting_agent" ? "Letting agent" : "Landlord"}</p>
              <p className="text-xs font-mono text-slate-400" data-testid={`review-audit-${r.id}`}>IP: {r.review_ip || "—"} · Agreed T&Cs: {r.terms_agreed_at ? new Date(r.terms_agreed_at).toLocaleString("en-GB") : "—"}{r.responsible_email ? ` · Notify: ${r.responsible_email}` : ""}</p>
              <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2">
                {RATING_KEYS.map((k) => (
                  <div key={k} className="flex items-center justify-between text-sm"><span className="text-slate-500">{RATING_LABELS[k]}</span><Stars value={r.ratings[k]} size="w-3.5 h-3.5" /></div>
                ))}
              </div>

              <div className="mt-5 flex gap-2 flex-wrap">
                {r.status === "pending" && (
                  <>
                    <button data-testid={`approve-${r.id}`} onClick={() => approve(r.id)} className="flex items-center gap-1.5 bg-emerald-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-emerald-700 text-sm"><Check className="w-4 h-4" /> Approve</button>
                    <button data-testid={`reject-${r.id}`} onClick={() => setRejectId(rejectId === r.id ? null : r.id)} className="flex items-center gap-1.5 border border-red-300 text-red-700 font-semibold px-4 py-2 rounded-lg hover:bg-red-50 text-sm"><X className="w-4 h-4" /> Reject</button>
                  </>
                )}
                <button data-testid={`edit-${r.id}`} onClick={() => { setEditId(r.id); setEdit({ title: r.title || "", body: r.body }); }} className="flex items-center gap-1.5 border border-slate-300 text-slate-700 font-semibold px-4 py-2 rounded-lg hover:bg-slate-50 text-sm"><PencilLine className="w-4 h-4" /> Edit</button>
                {r.status === "live" && (
                  <button data-testid={`takedown-${r.id}`} onClick={() => takedown(r.id)} className="flex items-center gap-1.5 border border-red-300 text-red-700 font-semibold px-4 py-2 rounded-lg hover:bg-red-50 text-sm"><Trash2 className="w-4 h-4" /> Take down</button>
                )}
                {["removed", "rejected", "hidden"].includes(r.status) && (
                  <button data-testid={`restore-${r.id}`} onClick={() => restoreReview(r.id)} className="flex items-center gap-1.5 border border-emerald-300 text-emerald-700 font-semibold px-4 py-2 rounded-lg hover:bg-emerald-50 text-sm"><CheckCircle2 className="w-4 h-4" /> Make live again</button>
                )}
                <label htmlFor={`photo-${r.id}`} data-testid={`change-photo-${r.id}`} className="flex items-center gap-1.5 border border-slate-300 text-slate-700 font-semibold px-4 py-2 rounded-lg hover:bg-slate-50 text-sm cursor-pointer">
                  <ImageIcon className="w-4 h-4" /> Change photo
                  <input id={`photo-${r.id}`} type="file" accept="image/*" className="hidden" onChange={(e) => changePhoto(r.property_slug, e.target.files[0])} />
                </label>
                <button data-testid={`notify-${r.id}`} onClick={() => setNotifyId(notifyId === r.id ? null : r.id)} className="flex items-center gap-1.5 border border-slate-300 text-slate-700 font-semibold px-4 py-2 rounded-lg hover:bg-slate-50 text-sm"><Mail className="w-4 h-4" /> Notify landlord</button>
                {isSenior && (
                  <button data-testid={`delete-review-${r.id}`} onClick={() => deleteReview(r.id)} className="flex items-center gap-1.5 bg-red-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-red-700 text-sm"><Trash2 className="w-4 h-4" /> Delete permanently</button>
                )}
              </div>
              {notifyId === r.id && (
                <div className="mt-3 flex gap-2" data-testid={`notify-box-${r.id}`}>
                  <input data-testid={`notify-email-${r.id}`} type="email" placeholder="Landlord's email address" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)}
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                  <button data-testid={`notify-send-${r.id}`} onClick={() => notifyLandlord(r.id)} className="bg-navy text-white px-4 rounded-lg hover:bg-navy-600 text-sm font-semibold">Send</button>
                </div>
              )}
              {rejectId === r.id && (
                <div className="mt-3 flex gap-2" data-testid={`reject-box-${r.id}`}>
                  <input data-testid={`reject-reason-${r.id}`} placeholder="Reason (optional)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                  <button data-testid={`confirm-reject-${r.id}`} onClick={() => reject(r.id)} className="bg-red-600 text-white px-4 rounded-lg hover:bg-red-700 text-sm font-semibold">Confirm</button>
                </div>
              )}
            </div>

            <div className="p-6 bg-slate-50">
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500 flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Property photo</p>
              <div className="mt-3 mb-6">
                <PropertyPhoto slug={r.property_slug} version={photoVer[r.property_slug] || 0} />
              </div>
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500 flex items-center gap-2"><FileText className="w-4 h-4" /> Tenancy agreement</p>
              <div className="mt-3">
                {r.agreement_file_id ? <DocViewer fileId={r.agreement_file_id} /> : (
                  <div className="h-40 flex flex-col items-center justify-center text-slate-400 text-sm border border-dashed border-slate-300 rounded-lg gap-2" data-testid={`doc-deleted-${r.id}`}>
                    <AlertTriangle className="w-5 h-5" /> Document deleted (GDPR) after moderation
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AbandonedSection() {
  const [drafts, setDrafts] = useState([]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const load = useCallback(() => { api.get("/admin/abandoned-reviews").then((r) => setDrafts(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const runNudges = async () => {
    setRunning(true); setResult(null);
    try { const r = await api.post("/admin/run-nudges"); setResult(r.data); load(); }
    catch { /* ignore */ } finally { setRunning(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this abandoned draft? The tenant will lose their saved progress.")) return;
    try { await api.delete(`/admin/abandoned-reviews/${id}`); load(); } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div data-testid="abandoned-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-heading font-extrabold text-3xl text-navy">Abandoned reviews</h1>
        <button data-testid="run-nudges-btn" onClick={runNudges} disabled={running}
          className="flex items-center gap-2 bg-navy text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-navy-600 transition-colors disabled:opacity-60">
          <Send className="w-4 h-4" /> {running ? "Running…" : "Run nudges now"}
        </button>
      </div>
      <p className="mt-2 text-sm text-slate-500">Saved review drafts from tenants who haven't submitted. Drafts auto-delete after 5 days; you can also remove them manually. "Run nudges now" emails tenants with an unfinished review and tenants who signed up but never reviewed.</p>
      {result && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-xl px-4 py-3" data-testid="nudges-result">
          Nudges sent — {result.signup_nudges_sent} sign-up reminder(s), {result.draft_nudges_sent} unfinished-review reminder(s). {result.accounts_purged} account(s) purged.
        </div>
      )}
      <div className="mt-6 flex flex-col gap-3" data-testid="abandoned-list">
        {drafts.length === 0 && <p className="text-slate-500" data-testid="abandoned-empty">No abandoned drafts right now.</p>}
        {drafts.map((d) => (
          <div key={d.id} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between gap-3 flex-wrap" data-testid={`abandoned-${d.id}`}>
            <div className="min-w-0">
              <p className="font-semibold text-navy">{d.tenant_name || "Tenant"} <span className="text-sm font-normal text-slate-500">{d.email}</span></p>
              <p className="text-sm text-slate-600 truncate">{d.title || "(no title)"} · {d.address || "no address yet"}</p>
              <p className="text-xs text-slate-400 font-mono mt-1">Last edited {d.updated_at ? new Date(d.updated_at).toLocaleString("en-GB") : "—"}{d.abandoned_email_sent ? " · reminder emailed" : ""}</p>
            </div>
            <button data-testid={`delete-abandoned-${d.id}`} onClick={() => del(d.id)}
              className="flex items-center gap-1.5 border border-red-300 text-red-700 font-semibold px-4 py-2 rounded-lg hover:bg-red-50 text-sm shrink-0"><Trash2 className="w-4 h-4" /> Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}
function ClaimsSection() {
  const [status, setStatus] = useState("pending");
  const [claims, setClaims] = useState([]);
  const [rejectId, setRejectId] = useState(null);
  const [reason, setReason] = useState("");
  const load = useCallback(() => { api.get(`/admin/claims?status=${status}`).then((r) => setClaims(r.data)).catch(() => {}); }, [status]);
  useEffect(() => { load(); }, [load]);
  const approve = async (id) => { if (!window.confirm("Approve this landlord claim? They will be able to manage this property.")) return; await api.post(`/admin/claims/${id}/approve`); load(); };
  const reject = async (id) => { if (!window.confirm("Reject this claim?")) return; await api.post(`/admin/claims/${id}/reject`, { reason }); setRejectId(null); setReason(""); load(); };

  return (
    <div>
      <h1 className="font-heading font-extrabold text-3xl text-navy">Landlord claims</h1>
      <select data-testid="claims-status" value={status} onChange={(e) => setStatus(e.target.value)}
        className="mt-4 border border-slate-300 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint">
        {["pending", "approved", "rejected", "all"].map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <div className="mt-6 flex flex-col gap-5" data-testid="admin-claims">
        {claims.length === 0 && <p className="text-slate-500" data-testid="claims-empty">No {status} claims.</p>}
        {claims.map((c) => (
          <div key={c.id} className="bg-white border border-slate-200 rounded-2xl p-6 grid lg:grid-cols-2 gap-6 shadow-sm" data-testid={`admin-claim-${c.id}`}>
            <div>
              <h3 className="font-heading font-bold text-lg text-navy">{c.full_address}</h3>
              <p className="text-sm text-slate-500 mt-1">Claimed by <span className="font-semibold">{c.landlord_name}</span> as <span className="font-semibold">{c.ownership_type === "letting_agent" ? "Letting agent" : "Owner"}</span></p>
              {c.reverification && <span className="inline-flex items-center gap-1 mt-2 mr-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`claim-reverify-badge-${c.id}`}><AlertTriangle className="w-3 h-3" /> Annual re-verification</span>}
              <span className="inline-block mt-2 text-xs font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-600">{c.status}</span>
              {c.status === "pending" && (
                <div className="mt-5 flex gap-2 flex-wrap">
                  <button data-testid={`claim-approve-${c.id}`} onClick={() => approve(c.id)} className="flex items-center gap-1.5 bg-emerald-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-emerald-700 text-sm"><Check className="w-4 h-4" /> Approve</button>
                  <button data-testid={`claim-reject-${c.id}`} onClick={() => setRejectId(rejectId === c.id ? null : c.id)} className="flex items-center gap-1.5 border border-red-300 text-red-700 font-semibold px-4 py-2 rounded-lg hover:bg-red-50 text-sm"><X className="w-4 h-4" /> Reject</button>
                </div>
              )}
              {rejectId === c.id && (
                <div className="mt-3 flex gap-2">
                  <input data-testid={`claim-reject-reason-${c.id}`} placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)}
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                  <button data-testid={`claim-confirm-reject-${c.id}`} onClick={() => reject(c.id)} className="bg-red-600 text-white px-4 rounded-lg hover:bg-red-700 text-sm font-semibold">Confirm</button>
                </div>
              )}
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500 flex items-center gap-2"><FileText className="w-4 h-4" /> Proof of ownership / management</p>
              <div className="mt-3">{c.proof_file_id ? <DocViewer fileId={c.proof_file_id} /> : <p className="text-slate-400 text-sm">No document</p>}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MessagesSection() {
  const [msgs, setMsgs] = useState([]);
  const [reply, setReply] = useState({});
  const load = () => api.get("/admin/messages").then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async (id) => { if (!reply[id]?.trim()) return; await api.post(`/admin/messages/${id}/reply`, { reply: reply[id] }); setReply({ ...reply, [id]: "" }); load(); };
  const del = async (id) => { if (!window.confirm("Permanently delete this message? This cannot be undone.")) return; try { await api.delete(`/admin/messages/${id}`); load(); } catch (err) { alert(formatApiError(err.response?.data?.detail)); } };

  return (
    <div>
      <h1 className="font-heading font-extrabold text-3xl text-navy">Messages</h1>
      <p className="mt-1 text-sm text-slate-500">Contact-form submissions. Replies email the sender if SMTP is configured.</p>
      <div className="mt-6 flex flex-col gap-4" data-testid="admin-messages">
        {msgs.length === 0 && <p className="text-slate-500" data-testid="messages-empty">No messages yet.</p>}
        {msgs.map((m) => (
          <div key={m.id} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid={`admin-message-${m.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-mint-dark" /><span className="font-semibold text-navy">{m.name}</span><span className="text-sm text-slate-500">{m.email}</span></div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-mono px-2 py-0.5 rounded ${m.status === "replied" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{m.status}</span>
                <button data-testid={`message-delete-${m.id}`} onClick={() => del(m.id)} title="Delete message"
                  className="flex items-center gap-1 border border-red-300 text-red-700 text-xs font-semibold px-2.5 py-1 rounded-lg hover:bg-red-50 transition-colors"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
            {m.subject && <p className="mt-2 font-semibold text-navy">{m.subject}</p>}
            <p className="mt-1 text-slate-700 whitespace-pre-line">{m.message}</p>
            {m.reply ? (
              <div className="mt-3 border-l-4 border-mint bg-slate-50 p-3 rounded-r-lg">
                <p className="text-xs font-semibold text-navy flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-mint-dark" /> Replied{m.email_sent ? " · emailed" : " · saved (email not sent)"}</p>
                <p className="mt-1 text-slate-600 text-sm">{m.reply}</p>
              </div>
            ) : (
              <div className="mt-3 flex gap-2">
                <input data-testid={`message-reply-${m.id}`} placeholder="Type a reply…" value={reply[m.id] || ""} onChange={(e) => setReply({ ...reply, [m.id]: e.target.value })}
                  className="flex-1 border border-slate-300 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                <button data-testid={`message-send-${m.id}`} onClick={() => send(m.id)} className="bg-mint text-white px-4 rounded-xl hover:bg-mint-dark text-sm font-semibold flex items-center gap-1"><Send className="w-4 h-4" /> Reply</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AnalyticsSection() {
  const [town, setTown] = useState("");
  const [data, setData] = useState(null);
  const [towns, setTowns] = useState(null);
  const load = useCallback(() => { api.get(`/admin/analytics?town=${encodeURIComponent(town)}`).then((r) => setData(r.data)).catch(() => {}); }, [town]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/analytics/towns").then((r) => setTowns(r.data)).catch(() => {}); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/analytics/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "tenverly-town-data.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  if (!data) return <p className="text-slate-400" data-testid="analytics-loading">Loading…</p>;

  const issues = RATING_KEYS.map((k) => ({
    label: RATING_LABELS[k],
    value: (data.pct_poor && data.pct_poor[k] != null) ? data.pct_poor[k] : 0,
  }));

  return (
    <div data-testid="analytics-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-heading font-extrabold text-3xl text-navy">Data &amp; Insights</h1>
        <button data-testid="analytics-export" onClick={exportCsv} className="flex items-center gap-2 bg-navy text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-navy-600 transition-colors"><BarChart3 className="w-4 h-4" /> Export town data (CSV)</button>
      </div>
      <p className="mt-1 text-sm text-slate-500">Aggregate, anonymised market data — no personal information about any tenant or landlord is included.</p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="revenue-summary" style={data.earnings_visible ? {} : { display: "none" }}>
        {data.earnings_visible && (<>
        <div className="bg-mint-soft border border-mint rounded-2xl p-6" data-testid="analytics-mrr">
          <p className="text-xs font-mono uppercase tracking-wider text-mint-dark">Monthly recurring revenue</p>
          <p className="mt-2 font-heading font-extrabold text-4xl text-navy">£{(data.mrr_gbp ?? 0).toLocaleString()}</p>
          <p className="text-sm text-slate-500">{data.active_subscriptions ?? 0} active property {(data.active_subscriptions === 1) ? "subscription" : "subscriptions"} · £10 each</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-500">Annualised run-rate</p>
          <p className="mt-2 font-heading font-extrabold text-4xl text-navy">£{((data.mrr_gbp ?? 0) * 12).toLocaleString()}</p>
          <p className="text-sm text-slate-500">Projected from current MRR</p>
        </div>
        </>)}
      </div>

      {towns && (
        <div className="mt-6 grid sm:grid-cols-3 gap-4" data-testid="analytics-summary">
          <div className="bg-navy text-white rounded-2xl p-6">
            <p className="text-xs font-mono uppercase tracking-wider text-slate-400">Live reviews</p>
            <p className="mt-2 font-heading font-extrabold text-4xl">{towns.total_reviews}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <p className="text-xs font-mono uppercase tracking-wider text-slate-500">Towns covered</p>
            <p className="mt-2 font-heading font-extrabold text-4xl text-navy">{towns.total_towns}</p>
            <p className="text-sm text-slate-500">{towns.total_properties} properties</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <p className="text-xs font-mono uppercase tracking-wider text-amber-700">Worst town for damp/mould</p>
            <p className="mt-2 font-heading font-extrabold text-2xl text-amber-900" data-testid="worst-mould-town">{towns.worst_mould_town ? towns.worst_mould_town.town : "—"}</p>
            {towns.worst_mould_town && <p className="text-sm text-amber-700">{towns.worst_mould_town.pct_mould}% of reviews report mould</p>}
          </div>
        </div>
      )}

      {towns && towns.rows.length > 0 && (
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-6 overflow-x-auto" data-testid="analytics-table">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-500">Town breakdown (ranked by mould reports)</p>
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">Town</th><th className="py-2 px-2">Reviews</th><th className="py-2 px-2">Avg</th>
                <th className="py-2 px-2">% Mould</th><th className="py-2 px-2">% Slow repairs</th><th className="py-2 px-2">% Poor energy</th><th className="py-2 px-2">% Poor comms</th>
              </tr>
            </thead>
            <tbody>
              {towns.rows.map((r) => (
                <tr key={r.town} className="border-b border-slate-100" data-testid={`town-row-${r.town}`}>
                  <td className="py-2 pr-4 font-semibold text-navy">{r.town}</td>
                  <td className="py-2 px-2 font-mono">{r.reviews}</td>
                  <td className="py-2 px-2 font-mono">{r.avg_overall}</td>
                  <td className={`py-2 px-2 font-mono font-semibold ${r.pct_mould >= 50 ? "text-red-600" : "text-slate-700"}`}>{r.pct_mould}%</td>
                  <td className="py-2 px-2 font-mono">{r.pct_slow_repairs}%</td>
                  <td className="py-2 px-2 font-mono">{r.pct_poor_energy}%</td>
                  <td className="py-2 px-2 font-mono">{r.pct_poor_comms}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 flex items-end gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-500">Drill into a town</label>
          <select data-testid="analytics-town" value={town} onChange={(e) => setTown(e.target.value)}
            className="block mt-1 border border-slate-300 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint">
            <option value="">All towns</option>
            {data.towns.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="analytics-cards">
        <div className="bg-navy text-white rounded-2xl p-6">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-400">Reviews{town ? ` in ${town}` : ""}</p>
          <p className="mt-2 font-heading font-extrabold text-4xl">{data.total_reviews}</p>
          <p className="mt-1 text-slate-300 text-sm">Avg overall score {data.avg_overall}/5</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-500">Rated poorly (≤2★) {town ? `in ${town}` : "(all areas)"} — by category</p>
          <div className="mt-3 flex flex-col gap-3">
            {issues.map((i) => (
              <div key={i.label}>
                <div className="flex justify-between text-sm"><span className="text-slate-600">{i.label}</span><span className="font-mono font-semibold text-navy" data-testid={`stat-${i.label}`}>{i.value}%</span></div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-1"><div className="h-full bg-mint rounded-full" style={{ width: `${i.value}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-6">
        <p className="text-xs font-mono uppercase tracking-wider text-slate-500">Average category scores {town ? `in ${town}` : ""}</p>
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {RATING_KEYS.map((k) => (
            <div key={k}>
              <p className="text-xs text-slate-500">{RATING_LABELS[k]}</p>
              <p className="font-heading font-extrabold text-2xl text-navy">{data.category_avg[k]}<span className="text-base text-slate-400">/5</span></p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function DisputesSection() {
  const [status, setStatus] = useState("open");
  const [items, setItems] = useState([]);
  const load = useCallback(() => { api.get(`/admin/disputes?status=${status}`).then((r) => setItems(r.data)).catch(() => {}); }, [status]);
  useEffect(() => { load(); }, [load]);
  const resolve = async (id, action) => { if (!window.confirm(action === "remove" ? "Take this review down permanently?" : "Keep this review up and close the dispute?")) return; await api.post(`/admin/disputes/${id}/resolve`, { action }); load(); };

  return (
    <div>
      <h1 className="font-heading font-extrabold text-3xl text-navy">Disputes</h1>
      <p className="mt-1 text-sm text-slate-500">UK notice &amp; takedown. Open disputes auto-hide the review after 14 days unless the tenant confirms they stand by it.</p>
      <select data-testid="disputes-status" value={status} onChange={(e) => setStatus(e.target.value)}
        className="mt-4 border border-slate-300 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint">
        {["open", "confirmed", "expired", "removed", "all"].map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <div className="mt-6 flex flex-col gap-5" data-testid="admin-disputes">
        {items.length === 0 && <p className="text-slate-500" data-testid="disputes-empty">No {status} disputes.</p>}
        {items.map((d) => (
          <div key={d.id} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid={`admin-dispute-${d.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <h3 className="font-heading font-bold text-lg text-navy">{d.full_address}</h3>
                <p className="text-sm text-slate-500">Disputed by <span className="font-semibold">{d.landlord_name}</span> · tenant: {d.tenant_name}</p>
              </div>
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-600">{d.status}</span>
            </div>
            <p className="mt-2 text-slate-700"><span className="font-semibold">Reason:</span> {d.reason || "—"}</p>
            <p className="text-xs font-mono text-slate-400 mt-1">Deadline: {new Date(d.deadline).toLocaleDateString("en-GB")}</p>
            {d.review && (
              <div className="mt-3 bg-slate-50 rounded-xl p-4 text-sm">
                <p className="font-semibold text-navy">{d.review.title}</p>
                <p className="text-slate-600 mt-1">{d.review.body}</p>
              </div>
            )}
            {d.status === "open" && (
              <div className="mt-4 flex gap-2 flex-wrap">
                <button data-testid={`dispute-keep-${d.id}`} onClick={() => resolve(d.id, "keep")} className="flex items-center gap-1.5 bg-emerald-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-emerald-700 text-sm"><Check className="w-4 h-4" /> Keep review up</button>
                <button data-testid={`dispute-remove-${d.id}`} onClick={() => resolve(d.id, "remove")} className="flex items-center gap-1.5 border border-red-300 text-red-700 font-semibold px-4 py-2 rounded-lg hover:bg-red-50 text-sm"><Trash2 className="w-4 h-4" /> Take review down</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MembershipsSection() {
  const { user } = useAuth();
  const isSenior = user?.role === "senior_admin";
  const [subs, setSubs] = useState([]);
  const load = useCallback(() => { api.get("/admin/subscriptions").then((r) => setSubs(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, []); // eslint-disable-line
  const cancel = async (s) => {
    if (!window.confirm(`Cancel the subscription for ${s.full_address}?\n\nThis stops billing in Stripe (if linked) and removes the Verified badge for this property.`)) return;
    try { await api.post(`/admin/subscriptions/${s.slug}/cancel`); load(); }
    catch (err) { alert(formatApiError(err.response?.data?.detail) || "Could not cancel"); }
  };
  const paid = subs.filter((s) => !s.free_grant_until);
  const mrr = paid.length * 10;

  return (
    <div data-testid="memberships-section">
      <h1 className="font-heading font-extrabold text-3xl text-navy">Memberships</h1>
      <p className="mt-1 text-sm text-slate-500">Every property with an active membership. {isSenior ? "Cancel here to stop billing and clear any legacy/manual flags." : ""}</p>

      {isSenior && (
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div className="bg-mint-soft border border-mint rounded-2xl p-6">
            <p className="text-xs font-mono uppercase tracking-wider text-mint-dark">Monthly recurring revenue</p>
            <p className="mt-2 font-heading font-extrabold text-4xl text-navy" data-testid="memberships-mrr">£{mrr.toLocaleString()}</p>
            <p className="text-sm text-slate-500">{paid.length} paid {paid.length === 1 ? "subscription" : "subscriptions"} · £10 each</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <p className="text-xs font-mono uppercase tracking-wider text-slate-500">Annualised run-rate</p>
            <p className="mt-2 font-heading font-extrabold text-4xl text-navy">£{(mrr * 12).toLocaleString()}</p>
            <p className="text-sm text-slate-500">Projected from current MRR</p>
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-3" data-testid="admin-subscriptions">
        {subs.length === 0 && <p className="text-slate-500" data-testid="subs-empty">No active subscriptions.</p>}
        {subs.map((s) => (
          <div key={s.slug} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex items-center justify-between gap-4 flex-wrap" data-testid={`admin-sub-${s.slug}`}>
            <div className="min-w-0">
              <p className="font-semibold text-navy truncate">{s.full_address}</p>
              <p className="text-sm text-slate-500">{s.landlord ? `${s.landlord.name} · ${s.landlord.email}` : "Unclaimed / no landlord"}</p>
              <p className="text-xs font-mono text-slate-400 mt-0.5">
                {s.free_grant_until ? `Complimentary until ${new Date(s.free_grant_until).toLocaleDateString("en-GB")}` : (s.stripe_subscription_id ? `Stripe: ${s.stripe_subscription_id}` : "No Stripe subscription (legacy/manual flag)")}
                {s.next_billing_date ? ` · Next charge ${new Date(s.next_billing_date).toLocaleDateString("en-GB")}` : ""}
                {` · ${s.status}`}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {isSenior && <span className="font-heading font-bold text-navy">£{s.amount_gbp}<span className="text-xs font-normal text-slate-400">/mo</span></span>}
              {isSenior && (
                <button data-testid={`cancel-sub-${s.slug}`} onClick={() => cancel(s)}
                  className="border border-red-300 text-red-700 text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors">Cancel</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersSection() {
  const { user: me } = useAuth();
  const isSenior = me?.role === "senior_admin";
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "tenant", password: "" });
  const [userReviews, setUserReviews] = useState({});
  const [userEmails, setUserEmails] = useState({});
  const load = useCallback(() => { api.get(`/admin/users?q=${encodeURIComponent(q)}`).then((r) => setUsers(r.data)).catch(() => {}); }, [q]);
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startEdit = (u) => { setEditId(u.id); setForm({ name: u.name, email: u.email, role: u.role, password: "" }); };
  const save = async (id) => {
    const payload = { name: form.name, email: form.email, role: form.role };
    if (form.password) payload.password = form.password;
    await api.patch(`/admin/users/${id}`, payload); setEditId(null); load();
  };
  const viewReviews = async (id) => {
    if (userReviews[id]) { setUserReviews({ ...userReviews, [id]: null }); return; }
    const res = await api.get(`/admin/users/${id}/reviews`);
    setUserReviews({ ...userReviews, [id]: res.data });
  };
  const viewEmails = async (id) => {
    if (userEmails[id]) { setUserEmails({ ...userEmails, [id]: null }); return; }
    const res = await api.get(`/admin/users/${id}/emails`);
    setUserEmails({ ...userEmails, [id]: res.data });
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})?\n\nTheir reviews will be permanently removed (and the property scores recalculated). Any properties they claimed will be UN-CLAIMED — the property pages and all other reviews stay live. This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); load(); }
    catch (err) { alert(formatApiError(err.response?.data?.detail) || "Could not delete user"); }
  };
  const resendVerify = async (u) => {
    try {
      const r = await api.post(`/admin/users/${u.id}/resend-verification`);
      alert(r.data.status === "already_verified" ? `${u.email} is already verified.` : `Verification email sent to ${u.email}.`);
    } catch (err) { alert(formatApiError(err.response?.data?.detail) || "Could not send email"); }
  };

  return (
    <div>
      <h1 className="font-heading font-extrabold text-3xl text-navy">User accounts</h1>
      <div className="mt-4 flex gap-2">
        <input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search name or email" className="border border-slate-300 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint w-72" />
        <button data-testid="users-search-btn" onClick={load} className="bg-navy text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-navy-600">Search</button>
      </div>
      <div className="mt-6 flex flex-col gap-3" data-testid="admin-users">
        {users.length === 0 && <p className="text-slate-500" data-testid="users-empty">No users found.</p>}
        {users.map((u) => (
          <div key={u.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid={`admin-user-${u.id}`}>
            {editId === u.id ? (
              <div className="flex flex-col gap-2" data-testid={`user-edit-${u.id}`}>
                <div className="grid sm:grid-cols-3 gap-2">
                  <input data-testid={`user-name-${u.id}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                  <input data-testid={`user-email-${u.id}`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                  <select data-testid={`user-role-${u.id}`} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint">
                    {(isSenior ? ["tenant", "landlord", "admin", "senior_admin"] : ["tenant", "landlord"]).map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <input data-testid={`user-password-${u.id}`} type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="New password (leave blank to keep)" className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                <div className="flex gap-2">
                  <button data-testid={`user-save-${u.id}`} onClick={() => save(u.id)} className="bg-mint text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-mint-dark">Save</button>
                  <button onClick={() => setEditId(null)} className="text-sm text-slate-500 px-3">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-navy">{u.name}</span>
                    <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-600">{u.role}</span>
                    {u.email_verified ? <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> verified</span> : <span className="text-xs text-amber-600">unverified</span>}
                  </div>
                  <p className="text-sm text-slate-500">{u.email} · {u.review_count} reviews</p>
                  <p className="text-xs font-mono text-slate-400" data-testid={`user-audit-${u.id}`}>IP: {u.signup_ip || "—"} · Agreed T&Cs: {u.terms_agreed_at ? new Date(u.terms_agreed_at).toLocaleString("en-GB") : "—"}</p>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`user-reviews-${u.id}`} onClick={() => viewReviews(u.id)} className="border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-slate-50">Reviews</button>
                  <button data-testid={`user-emails-${u.id}`} onClick={() => viewEmails(u.id)} className="border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-slate-50 flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Emails{typeof u.email_count === "number" ? ` (${u.email_count})` : ""}</button>
                  {!u.email_verified && (
                    <button data-testid={`user-resend-${u.id}`} onClick={() => resendVerify(u)} className="border border-amber-300 text-amber-700 text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-amber-50 flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Resend verify</button>
                  )}
                  <button data-testid={`user-editbtn-${u.id}`} onClick={() => startEdit(u)} className="border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-slate-50 flex items-center gap-1"><PencilLine className="w-3.5 h-3.5" /> Edit</button>
                  {isSenior && (
                    <button data-testid={`user-delete-${u.id}`} onClick={() => deleteUser(u)} className="border border-red-300 text-red-700 text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-red-50 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                  )}
                </div>
              </div>
            )}
            {userReviews[u.id] && (
              <div className="mt-3 border-t border-slate-100 pt-3 flex flex-col gap-2" data-testid={`user-reviewlist-${u.id}`}>
                {userReviews[u.id].length === 0 && <p className="text-sm text-slate-400">No reviews.</p>}
                {userReviews[u.id].map((rv) => (
                  <div key={rv.id} className="text-sm flex items-center justify-between gap-2">
                    <span className="text-slate-700 truncate">{rv.full_address} — {rv.title || rv.body.slice(0, 40)}</span>
                    <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">{rv.status}</span>
                  </div>
                ))}
              </div>
            )}
            {userEmails[u.id] && (
              <div className="mt-3 border-t border-slate-100 pt-3 flex flex-col gap-2" data-testid={`user-emaillist-${u.id}`}>
                {userEmails[u.id].length === 0 && <p className="text-sm text-slate-400">No emails sent to this user yet.</p>}
                {userEmails[u.id].map((em) => {
                  const cls = em.status === "sent" ? "bg-emerald-50 text-emerald-700" : em.status === "failed" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700";
                  return (
                    <div key={em.id} className="text-sm flex items-center justify-between gap-2" data-testid={`user-email-${em.id}`}>
                      <span className="text-slate-700 truncate" title={em.error || ""}>{em.subject}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-slate-400 font-mono">{em.created_at ? new Date(em.created_at).toLocaleString("en-GB") : ""}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${cls}`}>{em.status}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PropertiesSection() {
  const { user } = useAuth();
  const isSenior = user?.role === "senior_admin";
  const [q, setQ] = useState("");
  const [props, setProps] = useState([]);
  const [editSlug, setEditSlug] = useState(null);
  const [form, setForm] = useState({ address_line: "", city: "", postcode: "", unit: "" });
  const [merge, setMerge] = useState({ source_slug: "", target_slug: "" });
  const [msg, setMsg] = useState("");
  const search = useCallback(() => { api.get(`/properties/search?q=${encodeURIComponent(q)}`).then((r) => setProps(r.data)).catch(() => {}); }, [q]);
  useEffect(() => { search(); }, []); // eslint-disable-line

  const grantMembership = async (p) => {
    const days = window.prompt(`Grant a complimentary membership for "${p.full_address}".\n\nHow many days? (the landlord is never told it's free)`, "30");
    if (days === null) return;
    const n = parseInt(days, 10);
    if (!n || n < 1) { alert("Please enter a valid number of days."); return; }
    try { await api.post(`/admin/properties/${p.slug}/grant-membership`, { days: n }); alert(`Complimentary membership granted for ${n} days.`); search(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || "Could not grant membership"); }
  };

  const startEdit = (p) => { setEditSlug(p.slug); setForm({ address_line: p.address_line, city: p.city, postcode: p.postcode, unit: "" }); setMsg(""); };
  const save = async (slug) => {
    setMsg("");
    try { await api.patch(`/admin/properties/${slug}`, form); setEditSlug(null); search(); }
    catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
  };
  const doMerge = async () => {
    setMsg("");
    if (!window.confirm(`Merge "${merge.source_slug}" into "${merge.target_slug}"? The source property page will be permanently deleted and its reviews moved.`)) return;
    try { await api.post(`/admin/properties/merge`, merge); setMerge({ source_slug: "", target_slug: "" }); setMsg("Merged successfully."); search(); }
    catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
  };
  const deleteProperty = async (p) => {
    if (!window.confirm(`PERMANENTLY delete "${p.full_address}"?\n\nThis removes the property page AND all ${p.aggregates.count} of its reviews for good. This cannot be undone. To keep the reviews, use Merge instead.`)) return;
    try { await api.delete(`/admin/properties/${p.slug}`); search(); }
    catch (e) { setMsg(formatApiError(e.response?.data?.detail) || "Could not delete property"); }
  };

  return (
    <div>
      <h1 className="font-heading font-extrabold text-3xl text-navy">Properties</h1>
      <p className="mt-1 text-sm text-slate-500">Fix messy addresses or merge duplicate property pages into one.</p>

      <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="merge-tool">
        <h2 className="font-heading font-bold text-lg text-navy flex items-center gap-2"><GitMerge className="w-5 h-5 text-mint-dark" /> Merge duplicates</h2>
        <div className="mt-3 grid sm:grid-cols-3 gap-2">
          <input data-testid="merge-source" value={merge.source_slug} onChange={(e) => setMerge({ ...merge, source_slug: e.target.value })} placeholder="Source slug (will be removed)" className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
          <input data-testid="merge-target" value={merge.target_slug} onChange={(e) => setMerge({ ...merge, target_slug: e.target.value })} placeholder="Target slug (kept)" className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
          <button data-testid="merge-submit" onClick={doMerge} className="bg-navy text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-navy-600">Merge</button>
        </div>
      </div>
      {msg && <p className="mt-3 text-sm text-mint-dark" data-testid="properties-msg">{msg}</p>}

      <div className="mt-5 flex gap-2">
        <input data-testid="properties-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Search properties" className="border border-slate-300 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint w-72" />
        <button data-testid="properties-search-btn" onClick={search} className="bg-navy text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-navy-600">Search</button>
      </div>

      <div className="mt-5 flex flex-col gap-3" data-testid="admin-properties">
        {props.length === 0 && <p className="text-slate-500" data-testid="properties-empty">No properties found.</p>}
        {props.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid={`admin-property-${p.slug}`}>
            {editSlug === p.slug ? (
              <div className="flex flex-col gap-2" data-testid={`property-edit-${p.slug}`}>
                <div>
                  <input data-testid={`property-unit-${p.slug}`} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="Flat / door number (e.g. Flat 2) — optional" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                  <p className="mt-1 text-xs text-slate-400">Leave blank if the flat/door number is already in the street address below. If filled, it's added to the front of the street address.</p>
                </div>
                <div className="grid sm:grid-cols-3 gap-2">
                  <input data-testid={`property-address-${p.slug}`} value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })} placeholder="Street address" className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint sm:col-span-1" />
                  <input data-testid={`property-city-${p.slug}`} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                  <input data-testid={`property-postcode-${p.slug}`} value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} placeholder="Postcode" className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                </div>
                <div className="flex gap-2">
                  <button data-testid={`property-save-${p.slug}`} onClick={() => save(p.slug)} className="bg-mint text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-mint-dark">Save address</button>
                  <button onClick={() => setEditSlug(null)} className="text-sm text-slate-500 px-3">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold text-navy">{p.full_address}</p>
                  <p className="text-xs font-mono text-slate-400">{p.slug} · {p.aggregates.count} reviews</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button data-testid={`property-editbtn-${p.slug}`} onClick={() => startEdit(p)} className="border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-slate-50 flex items-center gap-1"><PencilLine className="w-3.5 h-3.5" /> Edit address</button>
                  {isSenior && p.landlord_id && (
                    <button data-testid={`property-grant-${p.slug}`} onClick={() => grantMembership(p)} className="border border-emerald-300 text-emerald-700 text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-emerald-50 flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> Grant membership</button>
                  )}
                  {isSenior && (
                    <button data-testid={`property-delete-${p.slug}`} onClick={() => deleteProperty(p)} className="border border-red-300 text-red-700 text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-red-50 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function PendingProofViewer({ reviewId }) {
  const [url, setUrl] = useState(null);
  const [type, setType] = useState("");
  useEffect(() => {
    let revoke;
    api.get(`/admin/reviews/${reviewId}/pending-proof`, { responseType: "blob" }).then((res) => {
      const b = URL.createObjectURL(res.data); revoke = b; setUrl(b); setType(res.data.type);
    }).catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [reviewId]);
  if (!url) return <div className="h-48 flex items-center justify-center text-slate-400 text-sm">Loading proof…</div>;
  if (type.includes("pdf")) return <iframe title="proof" src={url} className="w-full h-96 rounded-lg border border-slate-200" data-testid={`pending-proof-pdf-${reviewId}`} />;
  return <img src={url} alt="pending proof of work" className="w-full max-w-md rounded-lg border border-slate-200" data-testid={`pending-proof-image-${reviewId}`} />;
}

function ModerationSection() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => { api.get("/admin/moderation").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (path) => { try { await api.post(path); load(); } catch (err) { alert(formatApiError(err.response?.data?.detail)); } };

  return (
    <div data-testid="admin-moderation-section">
      <h1 className="font-heading font-extrabold text-3xl text-navy">Landlord moderation queue</h1>
      <p className="text-sm text-slate-500 mt-1">Approve or reject landlord replies and proof-of-work uploads before they appear publicly on the site.</p>
      {items.length === 0 && <p className="mt-6 text-slate-500" data-testid="moderation-empty">Nothing awaiting approval right now.</p>}
      <div className="mt-6 flex flex-col gap-5">
        {items.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`moderation-item-${r.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-semibold text-navy">{r.tenant_name} <span className="text-slate-400 font-normal">· {r.property_slug}</span></p>
                {r.title && <p className="text-sm text-slate-500 mt-0.5">Review: “{r.title}”</p>}
              </div>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full"><Clock className="w-3.5 h-3.5" /> Pending approval</span>
            </div>

            {r.pending_reply && (
              <div className="mt-4 border-l-4 border-mint pl-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Landlord reply awaiting approval</p>
                <p className="mt-1 text-navy" data-testid={`moderation-reply-text-${r.id}`}>{r.pending_reply.text}</p>
                <p className="text-xs text-slate-400 mt-1">by {r.pending_reply.author}</p>
                <div className="mt-3 flex gap-2">
                  <button data-testid={`moderation-reply-approve-${r.id}`} onClick={() => act(`/admin/reviews/${r.id}/reply/approve`)}
                    className="bg-mint text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-mint-dark flex items-center gap-1"><Check className="w-4 h-4" /> Approve reply</button>
                  <button data-testid={`moderation-reply-reject-${r.id}`} onClick={() => { if (window.confirm("Reject and discard this landlord reply?")) act(`/admin/reviews/${r.id}/reply/reject`); }}
                    className="border border-red-300 text-red-700 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-red-50 flex items-center gap-1"><X className="w-4 h-4" /> Reject</button>
                </div>
              </div>
            )}

            {r.pending_proof_file_id && (
              <div className="mt-4 border-l-4 border-navy pl-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Proof of work awaiting approval</p>
                <div className="mt-2"><PendingProofViewer reviewId={r.id} /></div>
                <div className="mt-3 flex gap-2">
                  <button data-testid={`moderation-proof-approve-${r.id}`} onClick={() => act(`/admin/reviews/${r.id}/proof/approve`)}
                    className="bg-mint text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-mint-dark flex items-center gap-1"><Check className="w-4 h-4" /> Approve &amp; mark resolved</button>
                  <button data-testid={`moderation-proof-reject-${r.id}`} onClick={() => { if (window.confirm("Reject and permanently delete this proof image?")) act(`/admin/reviews/${r.id}/proof/reject`); }}
                    className="border border-red-300 text-red-700 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-red-50 flex items-center gap-1"><X className="w-4 h-4" /> Reject</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function ReverifySection() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => { api.get("/admin/reverifications").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const badge = { due: "bg-amber-50 text-amber-700 border-amber-200", suspended: "bg-red-50 text-red-700 border-red-200", pending_review: "bg-sky-50 text-sky-700 border-sky-200" };
  const label = { due: "Re-verification due", suspended: "Suspended (overdue)", pending_review: "Fresh proof — review in Claims" };
  const fmt = (d) => d ? new Date(d).toLocaleDateString() : "—";

  return (
    <div data-testid="admin-reverify-section">
      <h1 className="font-heading font-extrabold text-3xl text-navy">Ownership re-verification</h1>
      <p className="text-sm text-slate-500 mt-1">Landlord ownership must be renewed every 12 months. Landlords get 14 days after the due date to upload fresh proof before their reply/proof/dispute tools auto-suspend. Approve their fresh proof in the Claims tab.</p>
      {items.length === 0 && <p className="mt-6 text-slate-500" data-testid="reverify-empty">No properties are due for re-verification.</p>}
      <div className="mt-6 flex flex-col gap-4">
        {items.map((it) => (
          <div key={it.slug} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`reverify-item-${it.slug}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-semibold text-navy">{it.full_address}</p>
                <p className="text-sm text-slate-500 mt-0.5">{it.landlord_name} · {it.landlord_email}</p>
                <p className="text-xs text-slate-400 mt-1 font-mono">Verified {fmt(it.verified_at)} · Due {fmt(it.next_due_at)} · Grace ends {fmt(it.grace_deadline)}</p>
              </div>
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${badge[it.status]}`} data-testid={`reverify-status-${it.slug}`}>{label[it.status]}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function StripeSection() {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState("");
  const load = useCallback(() => { api.get("/admin/stripe/health").then((r) => setData(r.data)).catch((e) => setData({ configured: false, error: formatApiError(e.response?.data?.detail) })); }, []);
  useEffect(() => { load(); }, [load]);

  const resync = async () => {
    if (!window.confirm("Re-check every property against Stripe and activate any that paid but didn't unlock?")) return;
    setSyncing(true); setResult("");
    try {
      const r = await api.post("/admin/stripe/resync");
      setResult(r.data.count === 0 ? "✓ All paid properties are already active — nothing to fix." : `✓ Activated ${r.data.count} propert${r.data.count === 1 ? "y" : "ies"}: ${r.data.activated.join(", ")}`);
      load();
    } catch (e) { setResult(formatApiError(e.response?.data?.detail) || "Re-sync failed."); }
    finally { setSyncing(false); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleString("en-GB") : "—";

  return (
    <div data-testid="stripe-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-navy">Stripe health</h1>
          <p className="mt-1 text-sm text-slate-500">Recent checkout payments and their activation status. Use Re-sync if a landlord paid but their tools didn't unlock.</p>
        </div>
        <button data-testid="stripe-resync" onClick={resync} disabled={syncing}
          className="bg-navy text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-navy-600 transition-colors disabled:opacity-60 flex items-center gap-2">
          <CreditCard className="w-4 h-4" /> {syncing ? "Re-syncing…" : "Re-sync all active payments"}
        </button>
      </div>

      {result && <div className="mt-4 rounded-xl border border-mint bg-mint-soft px-4 py-3 text-sm font-semibold text-navy" data-testid="stripe-resync-result">{result}</div>}

      {data && !data.configured && (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="stripe-not-configured">
          Stripe isn't reachable here{data.error ? `: ${data.error}` : " (no API key in this environment)"}. On the live site this shows real payments.
        </div>
      )}

      {data?.configured && data.error && (
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="stripe-error">
          <p className="font-semibold">⚠️ {data.error}</p>
          <p className="mt-1 text-red-700">Fix: in Stripe → Developers → API keys, either use your standard <span className="font-mono">sk_live_…</span> secret key, or give your restricted key <span className="font-semibold">Read</span> access to <span className="font-semibold">Checkout Sessions, Subscriptions and Customers</span>. Then update <span className="font-mono">STRIPE_API_KEY</span> in your cPanel .env and restart.</p>
        </div>
      )}

      {data?.configured && (
        <div className="mt-6 flex flex-col gap-3" data-testid="stripe-sessions">
          {data.sessions.length === 0 && <p className="text-slate-500" data-testid="stripe-empty">No recent checkout sessions.</p>}
          {data.sessions.map((s) => (
            <div key={s.id} className={`bg-white border rounded-2xl p-4 flex items-center justify-between gap-4 flex-wrap ${s.mismatch ? "border-red-300" : "border-slate-200"}`} data-testid={`stripe-session-${s.id}`}>
              <div className="min-w-0">
                <p className="font-semibold text-navy truncate">{s.email || "—"} <span className="text-slate-400 font-normal">· {s.property_slug || "no property"}</span></p>
                <p className="text-xs font-mono text-slate-400 mt-0.5">{fmt(s.created)} · {s.currency} {s.amount_total.toFixed(2)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{s.payment_status || s.status}</span>
                {s.mismatch
                  ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-700" data-testid={`stripe-mismatch-${s.id}`}>PAID — not active! Re-sync</span>
                  : <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.property_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{s.property_active ? "active" : "inactive"}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

